import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { EventNames, EVENTS } from '@app/constants/events.constants';
import { VaultStoreEffects } from '@app/root-store/vault-store/effects';
import { SharedConfirmationDialogComponent } from '@app/shared/component/shared-confirmation-dialog/shared-confirmation-dialog.component';
import { DocumentService, UserService } from '@app/shared/services';
import { SharedVaultService } from '@app/shared/services/vault/shared-vault.service';
import { ToastrService } from 'ngx-toastr';
import { fromEvent, Observable, Subscription } from 'rxjs';
import { CreateNoteComponent } from './dialog/create-note.component';
import { CryptoService } from '@app/shared/services/crypto/crypto.services';
import { AutoUnsubscribe } from '@app/shared/decorators/auto-unsubscribe.decorator';

@AutoUnsubscribe()
@Component({
  selector: 'notes-component',
  templateUrl: './notes.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotesComponent implements OnInit, OnDestroy {
  @Input() selectedSecurity_key: any = null;
  @Input() fId;
  @Input() data: any;
  @Input() sharingMode: any;
  @Input() currentFormId: any;
  @Input() mode: any;
  @Input() currentuserId;
  @Input() itemsToSetUpList;
  @Input() securityStateObject;
  @Input() inlineComponent = false;
  @Input() isRemoveEditSaveSharing = false;
  @Output() parentFun: EventEmitter<any> = new EventEmitter();

  notesItemsToSetUp;
  isHovering = false;
  showAllNotes = false;
  vaultUserFormId: number;
  userId: number | string;
  isNoteDataLoading = true;
  isNoteDataFetched = true;
  allNoteDataRejectedData = false;
  isSuggestionDataLoading = true;
  notesData: Array<any> = [];
  allNoteData: Array<any> = [];
  initialDisplayCount: number = 2;
  noteSuggestionData: Array<any> = [];
  filteredSuggestionData: Array<any> = [];
  subscription = new Subscription();
  resizeObservable$: Observable<Event>;
  formUserPublicKey = false;
  screenWidth: number = window.innerWidth;
  isIpadOrTabletScreenWidth = signal(false);
  constructor(
    private documentService: DocumentService,
    private sharedVaultService: SharedVaultService,
    private dialog: MatDialog,
    private cryptoService: CryptoService,
    private toastr: ToastrService,
    private _userService: UserService,
    private vaultService: VaultStoreEffects,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.resizeObservable$ = fromEvent(window, 'resize');
    this.subscription.add(
      this.vaultService.vaultObject.subscribe((usr) => {
        this.vaultUserFormId = this.data.id ?? this.currentFormId;
        if (usr && usr.userId) {
          this.userId = usr.userId;
        }
      })
    );
    this.getNotesForForm();
    this.getSuggestionForNote();
  }

  getNotesForForm() {
    this.subscription.add(
      this.documentService
        .getNotes({
          userId: this.currentuserId ? this.currentuserId : this.userId,
          vaultUserFormId: this.vaultUserFormId ?? this.currentFormId,
        })
        .subscribe((res: Array<any>) => {
          this.notesData = res.reverse();
          if (this.selectedSecurity_key != null && this.selectedSecurity_key != undefined) {
            this.notesData.forEach((element) => {
              try {
                const vault_user_form_note_note = JSON.parse(element.vault_user_form_note_note);
                let data = this.cryptoService.decryptData(vault_user_form_note_note, this.selectedSecurity_key, this.selectedSecurity_key.hint);

                element.vault_user_form_note_note = JSON.parse(data);
              } catch (error) {}
            });
          }
          this.isNoteDataFetched = false;
          this.filterSuggestionDataForForm();
        })
    );
  }

  getSuggestionForNote() {
    this.subscription.add(
      this.documentService
        .getSuggestionForNote({
          userId: this.currentuserId ? this.currentuserId : this.userId,
          vaultUserFormId: this.vaultUserFormId ?? this.currentFormId,
        })
        .subscribe(
          (res) => {
            this.noteSuggestionData = res;
            this.isSuggestionDataLoading = false;
            this.cd.detectChanges();
            this.filterSuggestionDataForForm();
          },
          (error: any) => {
            this.toastr.error('Error occurred while getting suggestion for form');
          }
        )
    );
  }

  saveAllNotes(selectedSecurity_key) {
    this.selectedSecurity_key = selectedSecurity_key;
    this.allNoteData.forEach((note) => {
      if (note?.vault_user_form_note_id) {
        const note_data = JSON.parse(note.vault_user_form_note_note);
        const payload = {
          note: JSON.stringify({ title: note_data.title.trim(), noteContent: note_data.noteContent.trim() }),
          user_pk: this.public_key(),
          id: note.vault_user_form_note_id,
        };

        this.subscription.add(this.documentService.updateNote(payload, this.selectedSecurity_key).subscribe(() => {}));
      }
    });
  }

  public_key() {
    return this.securityStateObject.public_key;
  }

  filterSuggestionDataForForm() {
    if (this.noteSuggestionData.length) {
      this.filteredSuggestionData = [];
      this.noteSuggestionData.forEach((element) => {
        const existingNote = this.notesData.find(
          (note) =>
            note?.vault_user_form_note_suggestion_note_type === element.title &&
            note?.vault_user_form_note_suggestion_note_status !== null &&
            note?.vault_user_form_note_suggestion_note_status !== undefined
        );
        if (!existingNote) {
          const suggestionObject = {
            vault_user_form_note_note: JSON.stringify({ title: element.title, noteContent: element.placeholder_text }),
          };
          this.filteredSuggestionData.push(suggestionObject);
        }
      });
      this.allNoteData = [...this.filteredSuggestionData, ...this.notesData];
      if (!this.isNoteDataFetched && !this.isSuggestionDataLoading) {
        this.allNoteDataRejectedData =
          this.allNoteData.length > 0 &&
          this.allNoteData.every(
            (note) => note.vault_user_form_note_suggestion_note_status && note.vault_user_form_note_suggestion_note_status === 'rejected'
          );
        this.isNoteDataLoading = false;
      }
    } else if (!this.isNoteDataFetched && !this.isSuggestionDataLoading) {
      this.allNoteData = this.notesData;
      this.isNoteDataLoading = false;
    }
    this.cd.detectChanges();
  }

  openModal(eventData = null) {
    if (eventData?.sub_type) {
      const suggestionObject = this.noteSuggestionData.filter((item) => item.title === eventData?.sub_type);
      eventData['title'] = suggestionObject[0].title;
      eventData['content'] = suggestionObject[0].placeholder_text;
    }
    const dialogRef = this.dialog.open(CreateNoteComponent, {
      panelClass: 'full-window-dialog-3',
      autoFocus: false,
      data: {
        ...this.data,
        ...(eventData && { title: eventData?.title, noteContent: eventData?.content }),
        formItemsSetup: eventData ? true : false,
        currentFormId: this.currentFormId,
        selectedSecurity_key: this.selectedSecurity_key,
        securityStateObject: this.securityStateObject,
        isEditing: false,
        ...(eventData?.is_suggestion_note && { is_suggestion_note: true }),
      },
    });
    this.subscription.add(
      dialogRef.afterClosed().subscribe((confirmation) => {
        if (confirmation.isSuccess) {
          this.getNotesForForm();
          const eventSource = this.inlineComponent ? 'setup' : 'view';
          this.trackEventsInMixPanel([
            { eventName: EVENTS[`template_${eventSource}_add_note`], ...(confirmation?.title && { data: { note_title: confirmation.title } }) },
          ]);
        }
      })
    );
  }

  deleteSuggestionForAdditionalNote(data) {
    const dialogRef = this.dialog.open(SharedConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Remove suggestion',
        materialIcon: 'remove-info',
        text: `Are you sure you want to remove the suggested note: ${data?.title}? This action can not be undone.`,
        proceedTitle: 'Remove',
        showCancelBtn: true,
      },
    });
    this.subscription.add(
      dialogRef.afterClosed().subscribe((confirmation) => {
        if (confirmation) {
          const payload = {
            note: JSON.stringify({ title: data?.title.trim(), noteContent: data?.content.trim() }),
            user_pk: this.public_key(),
            suggestion_note_type: data?.title,
            suggestion_note_status: 'rejected',
          };
          this.subscription.add(
            this.documentService.createFormEncryption(payload, this.currentFormId, this.selectedSecurity_key).subscribe((res) => {
              this.getNotesForForm();
            })
          );
        }
      })
    );
  }

  trackEventsInMixPanel(events: { eventName: EventNames; data?: any }[]) {
    this._userService.trackEventsInMixPanel(events).then((response) => {});
  }

  handleShowAllNotes() {
    this.showAllNotes = true;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this._userService.cleanupWorker();
  }
}
